import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

export interface KeyboardShortcut {
  key: string;
  ctrl?: boolean;
  alt?: boolean;
  shift?: boolean;
  description: string;
  action: () => void;
}

// Check if user is typing in an input field
function isTyping(e: KeyboardEvent): boolean {
  const target = e.target as HTMLElement;
  const tagName = target.tagName.toLowerCase();
  const isEditable = target.isContentEditable;
  const isInput = tagName === "input" || tagName === "textarea" || tagName === "select";
  return isInput || isEditable;
}

// Global keyboard shortcuts hook
export function useKeyboardShortcuts(shortcuts: KeyboardShortcut[], enabled = true) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!enabled) return;

      // Don't trigger shortcuts when typing in input fields
      if (isTyping(e)) return;

      for (const shortcut of shortcuts) {
        const ctrlMatch = shortcut.ctrl ? (e.ctrlKey || e.metaKey) : !e.ctrlKey && !e.metaKey;
        const altMatch = shortcut.alt ? e.altKey : !e.altKey;
        const shiftMatch = shortcut.shift ? e.shiftKey : !e.shiftKey;
        const keyMatch = e.key.toLowerCase() === shortcut.key.toLowerCase();

        if (ctrlMatch && altMatch && shiftMatch && keyMatch) {
          e.preventDefault();
          shortcut.action();
          break;
        }
      }
    },
    [shortcuts, enabled]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);
}

// Global navigation shortcuts
export function useGlobalShortcuts() {
  const navigate = useNavigate();

  const shortcuts: KeyboardShortcut[] = [
    {
      key: "d",
      description: "Go to Dashboard",
      action: () => navigate("/dashboard"),
    },
    {
      key: "c",
      description: "Go to Credentials",
      action: () => navigate("/credentials"),
    },
    {
      key: "n",
      description: "Issue New Credential",
      action: () => navigate("/credentials/issue"),
    },
    {
      key: "v",
      description: "Go to Verify",
      action: () => navigate("/verify"),
    },
    {
      key: "a",
      description: "Go to Audit Log",
      action: () => navigate("/audit"),
    },
    {
      key: "s",
      description: "Go to Settings",
      action: () => navigate("/settings"),
    },
    {
      key: "k",
      description: "Go to API Keys",
      action: () => navigate("/api-keys"),
    },
    {
      key: "w",
      description: "Go to Subject Wallet",
      action: () => navigate("/wallet"),
    },
    {
      key: "/",
      description: "Focus search",
      action: () => {
        const searchInput = document.querySelector('input[type="search"], input[placeholder*="Search"]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
      },
    },
    {
      key: "?",
      shift: true,
      description: "Show keyboard shortcuts",
      action: () => {
        // Dispatch custom event that can be caught by a shortcuts modal
        window.dispatchEvent(new CustomEvent("show-keyboard-shortcuts"));
      },
    },
    {
      key: "Escape",
      description: "Close modal/dialog",
      action: () => {
        // Dispatch escape event
        window.dispatchEvent(new CustomEvent("close-modal"));
      },
    },
  ];

  useKeyboardShortcuts(shortcuts);

  return shortcuts;
}

// Get display text for a keyboard shortcut
export function getShortcutDisplay(shortcut: KeyboardShortcut): string {
  const parts: string[] = [];
  if (shortcut.ctrl) parts.push("⌘/Ctrl");
  if (shortcut.alt) parts.push("Alt");
  if (shortcut.shift) parts.push("⇧");
  
  // Format key display
  let keyDisplay = shortcut.key.toUpperCase();
  if (shortcut.key === "/") keyDisplay = "/";
  if (shortcut.key === "?") keyDisplay = "?";
  if (shortcut.key === "Escape") keyDisplay = "Esc";
  
  parts.push(keyDisplay);
  return parts.join(" + ");
}

// All available shortcuts for display
export const ALL_SHORTCUTS: KeyboardShortcut[] = [
  { key: "d", description: "Go to Dashboard", action: () => {} },
  { key: "c", description: "Go to Credentials", action: () => {} },
  { key: "n", description: "Issue New Credential", action: () => {} },
  { key: "v", description: "Go to Verify", action: () => {} },
  { key: "a", description: "Go to Audit Log", action: () => {} },
  { key: "s", description: "Go to Settings", action: () => {} },
  { key: "k", description: "Go to API Keys", action: () => {} },
  { key: "w", description: "Go to Subject Wallet", action: () => {} },
  { key: "/", description: "Focus search", action: () => {} },
  { key: "?", shift: true, description: "Show keyboard shortcuts", action: () => {} },
  { key: "Escape", description: "Close modal/dialog", action: () => {} },
];
